import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useRealtime } from "@/hooks/useRealtime";
import { useAudioCall } from "@/hooks/useAudioCall";

// Encapsulates all visitor-side chat state so multiple UIs (floating widget,
// standalone phone view) can share identical behaviour.
export function useWidgetChat(apiKey, { autoStart = false, identity = null, viewing = true } = {}) {
  const [config, setConfig] = useState(null);
  const [session, setSession] = useState(null);
  const [visitorName, setVisitorName] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [agentTyping, setAgentTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const typingClearRef = useRef(null);
  const callHandlerRef = useRef(null);
  const autoStartedRef = useRef(false);
  const idStartedRef = useRef("");
  const tokenRef = useRef(null);
  const viewingRef = useRef(viewing);
  const sendRef = useRef(null);
  const sessionRef = useRef(null);
  const storeKey = `sc_widget_${apiKey}`;

  const markSeen = useCallback(() => {
    const s = sessionRef.current;
    if (s && sendRef.current) sendRef.current({ type: "seen", conversation_id: s.conversation_id });
  }, []);

  const accent = config?.brand_color || "#0e8f7e";

  // Track whether the chat is on-screen; a new agent message while it's closed
  // increments the unread badge, and opening it clears the badge.
  useEffect(() => {
    viewingRef.current = viewing;
    if (viewing) setUnreadCount(0);
  }, [viewing]);

  // Attach the visitor token so the (otherwise public) message endpoints can
  // verify the caller owns this conversation.
  const authCfg = (extra) => {
    const headers = tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {};
    return { ...(extra || {}), headers: { ...headers, ...(extra?.headers || {}) } };
  };

  const addMessage = useCallback((msg) => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, []);

  const { send, connected } = useRealtime({
    token: session?.visitor_token,
    conversationId: session?.conversation_id,
    enabled: !!session,
    handlers: {
      onMessage: (m) => {
        addMessage(m);
        // Count agent replies that arrive while the widget is closed; if it's
        // open, tell the server we've seen it (agent gets a blue tick).
        if (m.sender_type === "agent") {
          if (viewingRef.current) markSeen();
          else setUnreadCount((n) => n + 1);
        }
      },
      onTyping: (d) => {
        if (d.sender_type === "agent") {
          setAgentTyping(!!d.is_typing);
          clearTimeout(typingClearRef.current);
          if (d.is_typing) typingClearRef.current = setTimeout(() => setAgentTyping(false), 3000);
        }
      },
      // Agent resolved the chat → clear the visitor's view (fresh next time).
      onResolved: () => setMessages([]),
      // Live update (e.g. a voice note's translation arrived) — merge it in.
      onMessageUpdate: (u) => setMessages((prev) => prev.map((m) => (m.id === u.id ? { ...m, ...u } : m))),
      onSignal: (d) => callHandlerRef.current && callHandlerRef.current(d),
    },
  });

  sendRef.current = send;
  sessionRef.current = session;

  // When the chat is on-screen (and whenever it (re)connects), tell the server
  // the customer has seen the team's messages → blue ticks on the agent side.
  useEffect(() => {
    if (viewing && session && connected) markSeen();
  }, [viewing, session, connected, markSeen]);

  const call = useAudioCall({ send, conversationId: session?.conversation_id, selfName: visitorName || "Visitor" });
  useEffect(() => { callHandlerRef.current = call.processSignal; }, [call.processSignal]);

  const startSession = useCallback(async (visitorId, fields) => {
    const f = typeof fields === "string" ? { name: fields } : (fields || {});
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/widget/${apiKey}/session`, {
        visitor_id: visitorId || undefined,
        name: f.name || undefined,
        phone: f.phone || undefined,
        email: f.email || undefined,
        location: f.location || undefined,
        user_hash: f.user_hash || undefined,
        page_url: window.location.href,
      });
      tokenRef.current = data.visitor_token;
      setSession(data);
      setVisitorName(f.name || data.config?.name || "Visitor");
      localStorage.setItem(storeKey, JSON.stringify({ visitor_id: data.visitor_id, name: f.name || "" }));
      const msgs = await axios.get(`${API}/widget/${apiKey}/messages`, authCfg({ params: { conversation_id: data.conversation_id } }));
      setMessages(msgs.data);
      return data;
    } catch (e) {
      // 403 = identity verification failed; surface nothing here, caller decides.
      return null;
    } finally {
      setStarting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, storeKey]);

  useEffect(() => {
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/widget/${apiKey}/config`);
        setConfig(data);
        const saved = JSON.parse(localStorage.getItem(storeKey) || "null");
        if (saved?.visitor_id) await startSession(saved.visitor_id, saved.name);
        else if (autoStart && !data.pre_chat?.enabled) await startSession(null, "");
      } catch {
        setConfig({ error: true });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  // Identity passed in by the host app (name/phone/userId/userHash) — start (or
  // switch to) that user's session automatically, skipping the pre-chat form.
  useEffect(() => {
    if (!config || starting || !identity) return;
    const uid = identity.userId || identity.visitorId || "";
    const hasAny = uid || identity.name || identity.email || identity.phone || identity.location;
    if (!hasAny) return;
    const key = uid || "__fields__";
    if (idStartedRef.current === key) return;
    if (session && uid && session.visitor_id === uid) { idStartedRef.current = key; return; }
    idStartedRef.current = key;
    startSession(uid || null, {
      name: identity.name, email: identity.email, phone: identity.phone,
      location: identity.location, user_hash: identity.userHash,
    }).then((res) => {
      if (!res && uid) {
        // eslint-disable-next-line no-console
        console.warn(
          "SuperChat: could not start an identified session for userId=" + uid +
          ". If identity verification is on, userHash must equal " +
          "HMAC_SHA256(identity_secret, userId) as lowercase hex — sign the userId, not the phone."
        );
      }
    });
  }, [identity, config, session, starting, startSession]);

  // After a WebSocket drop, re-fetch the thread so replies that arrived while
  // offline are never lost.
  const prevConnectedRef = useRef(false);
  useEffect(() => {
    if (connected && prevConnectedRef.current === false && session) {
      axios
        .get(`${API}/widget/${apiKey}/messages`, authCfg({ params: { conversation_id: session.conversation_id } }))
        .then(({ data }) => setMessages(data))
        .catch(() => {});
    }
    prevConnectedRef.current = connected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, session, apiKey]);

  const sendMessage = useCallback(async (body) => {
    if (!session) return false;
    try {
      const { data } = await axios.post(`${API}/widget/${apiKey}/messages`, body, authCfg({
        params: { conversation_id: session.conversation_id },
      }));
      addMessage(data);
      return true;
    } catch {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, session, addMessage]);

  const sendTyping = useCallback((isTyping) => {
    if (session) send({ type: "typing", conversation_id: session.conversation_id, sender: visitorName, is_typing: isTyping });
  }, [session, send, visitorName]);

  return {
    config, session, messages, agentTyping, loading, starting, unreadCount,
    visitorName, nameInput, setNameInput,
    connected, accent, call,
    startSession, sendMessage, sendTyping,
  };
}
